// The Vue build version to load with the `import` command
import Vue from "vue";
import App from "./App";
import router from "./router";
import store from "./store";
import mixin from "./mixin";
import axios from "axios";
import vueEventCalendar from "vue-event-calendar";

import "vue-event-calendar/dist/style.css";
import {
  Calendar,
  ColorPicker,
  DatePicker,
  Input,
  InputNumber,
  Option,
  RadioGroup,
  Radio,
  RadioButton,
  Select,
  Switch,
  TimePicker,
  TimeSelect,
  Tag,
  Table,
  TableColumn,
  Popover,
  Checkbox,
  CheckboxButton,
  CheckboxGroup,
} from "element-ui";
import * as VueGoogleMaps from "vue2-google-maps";
import * as Sentry from "@sentry/vue";

import lang from "element-ui/lib/locale/lang/fr";
import locale from "element-ui/lib/locale";
import vSelect from "vue-select";
import vClickOutside from "v-click-outside";

import "vue-select/dist/vue-select.css";
import "vue-multiselect/dist/vue-multiselect.min.css";
import "./scss/style.scss";

import Tooltip from "vue-directive-tooltip";
import "vue-directive-tooltip/dist/vueDirectiveTooltip.css";
import Toast from "./plugins/toast";
import checkErrors from "./router/interceptor";
import ConfirmModalPlugin from "./plugins/confirm-modal";

import "flag-icons/css/flag-icons.min.css";

Vue.use(VueGoogleMaps, {
  load: {
    key: "AIzaSyBwheKRqS2Bm2c2dsbBl0KUX1f4CdybKgk",
    libraries: "places",
  },
  installComponents: true,
});

Vue.use(vClickOutside);
Vue.use(Tooltip);
Vue.use(Toast);
Vue.use(ConfirmModalPlugin);

Vue.component("v-select", vSelect);

if (!process.env.VUE_APP_API_URL) {
  console.error("Unknown VUE_APP_API_URL environment variable");
}

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.timeout = process.env.VUE_APP_API_TIMEOUT;
axios.defaults.withCredentials = true;

Vue.use(vueEventCalendar, { locale: "fr" });

const api = axios.create();

const savedToken = localStorage.getItem("token");

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url === "/users/refresh-token") {
        store.commit("AUTH_LOGOUT");
        router.push({ name: "login" });
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = token;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post("/users/refresh-token");
        store.commit("AUTH_TOKEN", data.accessToken);
        originalRequest.headers["Authorization"] = data.accessToken;
        processQueue(null, data.accessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.commit("AUTH_LOGOUT");
        router.push({ name: "login" });
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (!error.config?.skipErrorInterceptor) {
      await checkErrors(error);
    }

    return Promise.reject(error);
  },
);

Vue.api = api;
Vue.prototype.$api = api;

if (savedToken) {
  store.commit("AUTH_TOKEN", savedToken);
}

locale.use(lang);

Vue.use(Calendar);
Vue.use(ColorPicker);
Vue.use(DatePicker);
Vue.use(Input);
Vue.use(InputNumber);
Vue.use(Option);
Vue.use(RadioGroup);
Vue.use(Radio);
Vue.use(RadioButton);
Vue.use(Select);
Vue.use(Switch);
Vue.use(TimePicker);
Vue.use(TimeSelect);
Vue.use(Tag);
Vue.use(Table);
Vue.use(TableColumn);
Vue.use(Popover);
Vue.use(CheckboxGroup);
Vue.use(Checkbox);
Vue.use(CheckboxButton);

Vue.mixin(mixin);

console.log(process.env.VUE_APP_SENTRY_DSN);

try {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
} catch (e) {
  console.error(e);
}

new Vue({
  router,
  store,
  render: (h) => h(App),
}).$mount("#app");
